import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { dateTime, relative } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Badge, Busy, Button, Card, EmptyState, ErrorState, IconButton, cx } from '@/components/ui/primitives'
import { Input } from '@/components/ui/form'
import { useToast } from '@/components/ui/toast'
import type { NotificationItem, Paged } from '@/types/api'

/**
 * How each kind of notification reads, and what colour it carries.
 *
 * The server's type is a bare enum name. Showing it raw would put "ApprovalRequested" in
 * front of a rep, and the whole point of this screen is that somebody can scan it.
 */
const KINDS: Record<string, { label: string; tone: 'neutral' | 'positive' | 'warning' | 'critical' | 'accent' }> = {
  ApprovalRequested: { label: 'Approval requested', tone: 'warning' },
  ApprovalApproved: { label: 'Approved', tone: 'positive' },
  ApprovalRejected: { label: 'Changes needed', tone: 'critical' },
  QuotationSent: { label: 'Sent', tone: 'accent' },
  QuotationExpiring: { label: 'Expiring', tone: 'warning' },
  QuotationWon: { label: 'Won', tone: 'positive' },
  TermsOverrideAttached: { label: 'Terms override', tone: 'warning' },
  QuotationAccepted: { label: 'Accepted', tone: 'positive' },

  // The one kind that reports a failure: the quotation says Sent and the customer has
  // nothing, so it reads as critical rather than as news.
  QuotationEmailFailed: { label: 'Not delivered', tone: 'critical' },
}

function kind(type: string) {
  return KINDS[type] ?? { label: type, tone: 'neutral' as const }
}

/**
 * Everything the bell has ever shown, with the filters for finding one of them again.
 *
 * The panel behind the bell holds the last twelve, which answers "what just happened".
 * This screen answers "what was I told about that quotation last week", and it is the
 * only place the answer exists - notifications are the record of what the system said,
 * and a record you cannot page back through is a feed.
 */
/** Where the sort control starts, and what a third click on a header falls back to. */
const DEFAULT_SORT = '-received'

export function Notifications() {
  const navigate = useNavigate()
  const toast = useToast()
  const [unreadOnly, setUnreadOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [marking, setMarking] = useState(false)
  const [toggling, setToggling] = useState<string | null>(null)

  const list = useAsync<Paged<NotificationItem>>(
    (signal) =>
      api.get<Paged<NotificationItem>>(
        '/notifications',
        { unreadOnly, search, sort, page, pageSize },
        signal,
      ),
    [unreadOnly, search, sort, page, pageSize],
  )

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  const unread = list.data?.items.filter((item) => item.readAt === null).length ?? 0

  async function open(item: NotificationItem) {
    if (item.readAt === null) {
      try {
        await api.post(`/notifications/${item.id}/read`)
      } catch {
        /* reading it is a convenience, not the point of the click */
      }
    }

    if (!item.linkUrl) {
      // Nothing to open. Reflect the read state rather than leaving a click that did
      // nothing visible at all.
      list.reload()
      return
    }

    // The link is an absolute URL because the same row is emailed. In the app it is a route.
    navigate(item.linkUrl.replace(/^https?:\/\/[^/]+/, ''))
  }

  /**
   * The read state as something chosen rather than only something that happens.
   *
   * Opening a notification reads it, which is right - but opening one is also how
   * somebody finds out it needs dealing with on Monday, and until now that click was
   * one-way. So each row carries the reverse of its own state.
   */
  async function toggleRead(item: NotificationItem) {
    const path = item.readAt === null ? 'read' : 'unread'

    setToggling(item.id)
    try {
      await api.post(`/notifications/${item.id}/${path}`)
      list.reload()
    } catch {
      toast.show('That could not be changed. Try again in a moment.', 'error')
    } finally {
      setToggling(null)
    }
  }

  async function markAllRead() {
    setMarking(true)
    try {
      await api.post('/notifications/read-all')
      list.reload()
    } catch {
      toast.show('Those could not be marked read. Try again in a moment.', 'error')
    } finally {
      setMarking(false)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Notifications"
        title="Notification centre"
        hint="Every approval, decision and expiry warning you have been sent. Open one to go to the quotation it is about."
        actions={
          <Button onClick={() => void markAllRead()} loading={marking} disabled={unread === 0}>
            Mark all read
          </Button>
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}
        sortChoices={[
          { value: '-received', label: 'Newest first' },
          { value: 'received', label: 'Oldest first' },
          { value: 'kind', label: 'Kind' },
          { value: '-read', label: 'Unread first' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search title or message"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect
          label="Show"
          value={unreadOnly ? 'unread' : ''}
          onChange={(value) => refine(() => setUnreadOnly(value === 'unread'))}
          width="w-40"
        >
          <option value="">Everything</option>
          <option value="unread">Unread only</option>
        </FilterSelect>
      </ListToolbar>

      {list.error && (
        <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />
      )}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {list.data.items.length === 0 ? (
              <EmptyState
                title={search ? 'Nothing matches that' : unreadOnly ? 'Nothing unread' : 'Nothing yet'}
                hint={
                  search
                    ? 'The search covers the title and the message.'
                    : unreadOnly
                      ? 'Everything you have been sent has been read.'
                      : 'Approval requests, decisions and expiring quotations land here.'
                }
              />
            ) : (
              <>
                <ul>
                  {list.data.items.map((item) => (
                    // The row is a flex pair rather than one button, because the read
                    // toggle is a second action and a button inside a button is neither
                    // valid nor reachable by keyboard.
                    <li
                      key={item.id}
                      className={cx(
                        'flex items-start border-b border-line-soft transition-colors hover:bg-surface-2',
                        item.readAt === null && 'bg-accent-wash/40',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => void open(item)}
                        className="flex min-w-0 flex-1 flex-col gap-1 py-3.5 pl-5 pr-2 text-left"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          {/* The unread dot rather than bolder text: a list where half the
                              rows are bold is a list with no emphasis left to give. */}
                          <span
                            aria-hidden
                            className={cx(
                              'size-1.5 shrink-0 rounded-full',
                              item.readAt === null ? 'bg-accent' : 'bg-transparent',
                            )}
                          />
                          <Badge tone={kind(item.type).tone}>{kind(item.type).label}</Badge>
                          <span className="text-sm font-medium">{item.title}</span>

                          <span
                            className="ml-auto shrink-0 font-mono text-[11px] text-faint"
                            title={dateTime(item.createdAt)}
                          >
                            {relative(item.createdAt)}
                          </span>
                        </div>

                        <p className="pl-3.5 text-[13px] text-muted">{item.body}</p>
                      </button>

                      {/* The icon shows the state the click produces, the way a mail
                          client's does: an opened envelope reads it, a sealed one puts
                          it back. The label says which in words, because an envelope on
                          its own does not tell you which direction it goes. */}
                      <span className="flex shrink-0 items-center py-3 pr-3">
                        <IconButton
                          label={item.readAt === null ? 'Mark as read' : 'Mark as unread'}
                          disabled={toggling === item.id}
                          onClick={() => void toggleRead(item)}
                        >
                          {item.readAt === null ? <EnvelopeOpen /> : <EnvelopeSealed />}
                        </IconButton>
                      </span>
                    </li>
                  ))}
                </ul>

                <Pagination
                  page={list.data.page}
                  pageSize={list.data.pageSize}
                  totalCount={list.data.totalCount}
                  totalPages={list.data.totalPages}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size)
                    setPage(1)
                  }}
                />
              </>
            )}
          </Card>
        )}
      </Busy>
    </>
  )
}

function EnvelopeOpen() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-full">
      <path
        d="M2.75 7 8 2.9 13.25 7v5.35a.9.9 0 0 1-.9.9H3.65a.9.9 0 0 1-.9-.9V7Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M2.75 7 8 10.6 13.25 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function EnvelopeSealed() {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-full">
      <rect
        x="2.75"
        y="3.75"
        width="10.5"
        height="8.5"
        rx="1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M3.2 4.6 8 8.4l4.8-3.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
